import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2018. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Section, Alert } from '@govhhs/govhhs-design-system-react';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import AppSpinner from '../AppSpinner/AppSpinnerComponent';
import translations from '../ErrorHandling/ErrorBoundaryMessages';
import RouterLink from '../router/RouterLink';
import PATHS from '../router/Paths';
/**
 * Loading component. See further documentation below.
 *
 */

var LoadingPageComponent = function LoadingPageComponent(props) {
  var intl = props.intl;
  var formatMessage = intl.formatMessage;
  var goToHome = /*#__PURE__*/React.createElement(RouterLink, {
    to: PATHS.HOME
  }, formatMessage(translations.errorRedirectHomeLink));

  if (props.error) {
    // When the loader has errorred
    return /*#__PURE__*/React.createElement(Section, {
      ariaLabelledBy: "application-error-alert"
    }, /*#__PURE__*/React.createElement(Alert, {
      center: true,
      dismissable: false,
      id: "application-error-alert",
      role: "alert",
      text: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, translations.defaultError, {
        values: {
          refreshPageLink: /*#__PURE__*/React.createElement(RouterLink, {
            onClick: function onClick(e) {
              e.preventDefault(); // refresh current page from link

              props.retryLoading();
            },
            to: PATHS.HOME
          }, formatMessage(translations.refreshPageLink)),
          goToHome: goToHome
        }
      })),
      type: "danger"
    }));
  }

  if (props.timedOut) {
    // When the loader has taken longer than the timeout
    return /*#__PURE__*/React.createElement(AppSpinner, null);
  }

  if (props.pastDelay) {
    // When the loader has taken longer than the delay
    return /*#__PURE__*/React.createElement(AppSpinner, null);
  } // When the loader has just started


  return null;
};

LoadingPageComponent.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Whether a loading error has occurred. */
  // eslint-disable-next-line react/boolean-prop-naming
  error: PropTypes.bool,
  intl: intlShape.isRequired,

  /** Whether the timeout has been passed. */
  // eslint-disable-next-line react/boolean-prop-naming
  pastDelay: PropTypes.bool,

  /*
   * A function prop passed to LoadingComponent when the loader has failed, used to retry loading
   * the component.
   */
  retryLoading: PropTypes.func,

  /** Whether the timeout has timed out. */
  // eslint-disable-next-line react/boolean-prop-naming
  timedOut: PropTypes.bool
} : {};
LoadingPageComponent.defaultProps = {
  error: false,
  pastDelay: false,
  retryLoading: function retryLoading() {
    window.location.reload();
  },
  timedOut: false
};
/**
 * Loads a specified page by using react-loadable to split the code
 * deliverable into a chunk. For further information see
 * {@link https://facebook.github.io/create-react-app/docs/code-splitting|Code splitting}
 *
 */

export default injectIntl(LoadingPageComponent);